import React from 'react';
import { Button, Popconfirm, Space, Tooltip } from 'antd';
import { InfoCircleOutlined } from '@ant-design/icons';
// Types
import { CampaignOfferStatus, CampaignStatus } from 'api/graphql-global-types';
// Helpers
import { mapCampaignStatusToLabel } from 'helpers/campaign';

type ExpiredOfferActionsProps = {
  campaignStatus: CampaignStatus;
  loading: boolean;
  offerId: string;
  onReinstate: (offerId: string, status: CampaignOfferStatus) => void;
};

type ActionCopy = {
  label: string;
  targetStatus: CampaignOfferStatus;
  confirmTitle: string;
  confirmPoints: string[];
  primary?: boolean;
};

const ARCHIVED_ATTEMPT_POINT =
  'Any previous claim is archived, including proof that was never submitted.';

const ACTIONS: ActionCopy[] = [
  {
    label: 'Re-invite',
    targetStatus: CampaignOfferStatus.INVITED,
    confirmTitle: 'Re-invite this talent?',
    confirmPoints: [
      'The offer moves to Invited and the talent gets a new invitation notification.',
      'No budget is spent now. The fee is taken when the talent (or an admin) claims.',
      'An invite does not hold budget. If the budget runs out before they claim, the offer expires again.',
      ARCHIVED_ATTEMPT_POINT,
    ],
  },
  {
    label: 'Mark Claimed',
    targetStatus: CampaignOfferStatus.ACCEPTED,
    confirmTitle: 'Mark this offer as claimed?',
    confirmPoints: [
      "The talent's fee is taken from the campaign budget now.",
      'The talent has 2 days from now to submit proof. If they miss it, the offer expires again and the fee is refunded.',
      'If this uses the last of the budget, all remaining invites expire.',
      ARCHIVED_ATTEMPT_POINT,
    ],
    primary: true,
  },
];

const ExpiredOfferActions = ({
  campaignStatus,
  loading,
  offerId,
  onReinstate,
}: ExpiredOfferActionsProps): JSX.Element => {
  const unavailableReason =
    campaignStatus === CampaignStatus.PAID_AND_ACTIVE
      ? null
      : `Unavailable: the campaign is "${mapCampaignStatusToLabel(
          campaignStatus
        )}". Offers can only be reinstated while the campaign is "Paid and active".`;

  return (
    <Space wrap>
      {ACTIONS.map((action) => {
        const button = (
          <Button
            disabled={Boolean(unavailableReason) || loading}
            loading={loading}
            size="small"
            type={action.primary ? 'primary' : 'default'}
          >
            {action.label}
          </Button>
        );

        if (unavailableReason) {
          // Disabled buttons swallow mouse events, so the wrapper carries the tooltip.
          return (
            <Tooltip key={action.label} title={unavailableReason}>
              <span>{button}</span>
            </Tooltip>
          );
        }

        return (
          <Popconfirm
            key={action.label}
            cancelText="Cancel"
            okText={action.label}
            onConfirm={() => onReinstate(offerId, action.targetStatus)}
            title={
              <div style={{ maxWidth: 340 }}>
                <strong>{action.confirmTitle}</strong>
                <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
                  {action.confirmPoints.map((point) => (
                    <li key={point}>{point}</li>
                  ))}
                </ul>
              </div>
            }
          >
            {button}
          </Popconfirm>
        );
      })}
      <Tooltip
        title={
          <div>
            <p style={{ margin: 0 }}>
              An offer expires when the talent does not submit proof before
              their deadline, or when the campaign budget runs out before they
              claim.
            </p>
            <p style={{ margin: '8px 0 0' }}>
              Both actions need a &quot;Paid and active&quot; campaign with
              enough budget left for this talent&apos;s fee. If the budget is
              short, raise it under Funding and fees, then try again.
            </p>
            <p style={{ margin: '8px 0 0' }}>
              Raising the budget does not bring expired offers back on its own.
              Reinstate each one here.
            </p>
          </div>
        }
      >
        <InfoCircleOutlined
          aria-label="About reinstating expired Social Boost offers"
          style={{ color: 'rgba(0, 0, 0, 0.45)' }}
        />
      </Tooltip>
    </Space>
  );
};

export default ExpiredOfferActions;
